import { defaultAppliedFilterValues } from './globalFiltersDefaults';

const GLOBAL_FILTERS_STORAGE_KEY = 'globalFiltersApplied';

const defaultAppliedFilters = {
  ...defaultAppliedFilterValues,
  divisionLabels: [],
  planLabels: [],
  isAllDivisionsSelected: true,
  isAllPlansSelected: true,
  divisionsConstrainedByPlan: false,
  plansConstrainedByDivision: false,
};

const appliedFilterKeys = Object.keys(defaultAppliedFilters);

const isStorageAvailable = () => typeof localStorage !== 'undefined';

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

const getDefaultAppliedFilters = () => ({
  ...defaultAppliedFilters,
  divisionLabels: [],
  planLabels: []
});

/**
 * Returns an "empty" applied filters object with no filter values set.
 * Used when the current tenant has filters disabled (`filtersEnabled === false`),
 * so no filter parameters are sent to downstream queries.
 */
const getEmptyAppliedFilters = () =>
  appliedFilterKeys.reduce((empty, key) => {
    const defaultValue = defaultAppliedFilters[key];
    if (Array.isArray(defaultValue)) {
      empty[key] = [];
    } else if (typeof defaultValue === 'boolean') {
      empty[key] = defaultValue;
    } else if (defaultValue === null) {
      empty[key] = null;
    } else {
      empty[key] = '';
    }
    return empty;
  }, {});

const isAppliedFiltersPayload = (filters) => isPlainObject(filters);

const normalizeAppliedFilters = (filters = {}) => {
  if (!isAppliedFiltersPayload(filters)) {
    return {};
  }

  const sanitizedFilters = appliedFilterKeys.reduce((normalizedFilters, key) => {
    if (Object.prototype.hasOwnProperty.call(filters, key)) {
      normalizedFilters[key] = filters[key];
    }

    return normalizedFilters;
  }, {});

  return {
    ...sanitizedFilters,
    ...(Object.prototype.hasOwnProperty.call(sanitizedFilters, 'enablePriorComparison')
      ? { enablePriorComparison: sanitizedFilters.enablePriorComparison !== false }
      : {}),
    isAllDivisionsSelected:
      typeof sanitizedFilters.isAllDivisionsSelected === 'boolean'
        ? sanitizedFilters.isAllDivisionsSelected
        : (sanitizedFilters.divisionLabels || []).length === 0,
    isAllPlansSelected:
      typeof sanitizedFilters.isAllPlansSelected === 'boolean'
        ? sanitizedFilters.isAllPlansSelected
        : (sanitizedFilters.planLabels || []).length === 0,
    divisionsConstrainedByPlan: sanitizedFilters.divisionsConstrainedByPlan === true,
    plansConstrainedByDivision: sanitizedFilters.plansConstrainedByDivision === true
  };
};

const clearFiltersFromStorage = () => {
  if (!isStorageAvailable()) {
    return;
  }

  localStorage.removeItem(GLOBAL_FILTERS_STORAGE_KEY);
};

const loadFiltersFromStorage = () => {
  if (!isStorageAvailable()) {
    return getDefaultAppliedFilters();
  }

  try {
    const storedFilters = localStorage.getItem(GLOBAL_FILTERS_STORAGE_KEY);

    if (!storedFilters) {
      return getDefaultAppliedFilters();
    }

    const parsedFilters = JSON.parse(storedFilters);

    if (!isAppliedFiltersPayload(parsedFilters)) {
      return getDefaultAppliedFilters();
    }

    return {
      ...getDefaultAppliedFilters(),
      ...normalizeAppliedFilters(parsedFilters)
    };
  } catch (error) {
    console.error('Error loading global filters from localStorage:', error);
    return getDefaultAppliedFilters();
  }
};

const saveFiltersToStorage = (filters) => {
  if (!isStorageAvailable()) {
    return;
  }

  try {
    localStorage.setItem(GLOBAL_FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch (error) {
    console.error('Error saving global filters to localStorage:', error);
  }
};

export {
  GLOBAL_FILTERS_STORAGE_KEY,
  getDefaultAppliedFilters,
  getEmptyAppliedFilters,
  isAppliedFiltersPayload,
  normalizeAppliedFilters,
  clearFiltersFromStorage,
  loadFiltersFromStorage,
  saveFiltersToStorage
};
